import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useFieldArrayOf } from '@framework/form/form/relations/useFieldsArrayOf.ts';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { Button, Divider, Select, Space } from 'antd';
import { useCallback } from 'react';
import styled from 'styled-components';
import { ButtonActionSchema } from './ButtonActionSchema.ts';
import { ButtonActionExecAsyncServiceConfig } from './exec-async-service/ButtonActionExecAsyncServiceConfig.tsx';
import { ButtonActionType, type IButtonAction } from './IButtonAction.ts';
import { ButtonActionListAddConfig } from './list-add/ButtonActionListAddConfig.tsx';
import { ButtonActionListRemoveConfig } from './list-remove/ButtonActionListRemoveConfig.tsx';
import { ButtonActionSubsectionOpenConfig } from './subsection-open/ButtonActionSubsectionOpenConfig.tsx';

const StyledDivider = styled(Divider)`
  margin-bottom: 0px;
  margin-top: 8px;
  border-block-start: 1px solid ${props => props.theme.colorBorder};
`;

export interface IButtonActionsConfigProps {
  name: string;
}

interface IButtonActionTypeOption<T extends ButtonActionType> {
  value: T;
  category: string;
  label: string;
  description: string;
}

const ButtonActionTypeOptions = [
  {
    value: ButtonActionType.ListAdd as const,
    category: 'Liste',
    label: 'Aggiungi elemento a Lista',
    description: 'Aggiunge un nuovo elemento alla lista selezionata',
  },
  {
    value: ButtonActionType.ListRemove as const,
    category: 'Liste',
    label: 'Rimuovi elemento da Lista',
    description: "Rimuove l'elemento selezionato dalla lista",
  },
  {
    value: ButtonActionType.ExecAsyncService as const,
    category: 'Servizi',
    label: 'Esegui Servizio esterno',
    description: 'Esegue un servizio esterno in modo asincrono',
  },
  {
    value: ButtonActionType.SubsectionOpen as const,
    category: 'Navigazione',
    label: 'Apri Sottosezione',
    description: 'Apre una sottosezione',
  },
];

export function ButtonActionsConfig(props: IButtonActionsConfigProps) {
  const { name } = props;

  const { fields, append, remove } = useFieldArrayOf<IButtonAction>({ name });

  const renderField = useCallback(
    (field: IButtonAction, index: number) => {
      switch (field.type) {
        case ButtonActionType.ListAdd:
          return <ButtonActionListAddConfig name={`${name}.${index}`} />;
        case ButtonActionType.ListRemove:
          return <ButtonActionListRemoveConfig name={`${name}.${index}`} />;
        case ButtonActionType.ExecAsyncService:
          return (
            <ButtonActionExecAsyncServiceConfig name={`${name}.${index}`} />
          );
        case ButtonActionType.SubsectionOpen:
          return <ButtonActionSubsectionOpenConfig name={`${name}.${index}`} />;

        default:
          return null;
      }
    },
    [name],
  );

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }}>
        {fields.map((field, i) => (
          <div key={field.id}>
            <FlexRow
              gutter={8}
              right={
                <Button
                  danger
                  size="large"
                  onClick={() => {
                    remove(i);
                  }}
                  icon={<DeleteOutlined />}
                />
              }
            >
              {renderField(field, i)}
            </FlexRow>
            <StyledDivider />
          </div>
        ))}
        <Select
          value={null}
          size="large"
          style={{ width: '100%' }}
          placeholder={
            <Space>
              <PlusOutlined aria-hidden="true" /> Aggiungi azione...
            </Space>
          }
          options={ButtonActionTypeOptions}
          onChange={(type: ButtonActionType) => {
            const newAction = ButtonActionSchema.cast(
              { type },
              { assert: false },
            );
            append(newAction);
          }}
        />
      </Space>
    </div>
  );
}
